import { useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';
import { flattenAuthorityErrors } from '@/lib/utils/agentAuthorityNotifications';

import {
  glacierPolicyApiGetAgentAuthorityQueryKey,
  glacierPolicyApiGetPrintQueueQueryKey,
  glacierPolicyApiIssuePolicyMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { formatErrorMessage } from '../../utils';
import type { Message } from '../StandardMessage';
import { ErrorDialog } from './ErrorDialog';

// Component for the confirmation dialog to lock a policy
const LockPolicyDialog = ({
  isOpen,
  onOpenChange,
  isIssuing,
  setIssuing,
  setMessage,
  setLoading,
  warehouseKey,
  policyQK,
  formulasQK,
  permissionsQK,
  policyId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isIssuing: boolean;
  setIssuing: (issuing: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  setLoading: (loading: boolean) => void;
  warehouseKey: QueryKey;
  policyQK: QueryKey;
  formulasQK: QueryKey;
  permissionsQK?: QueryKey;
  policyId: string;
}) => {
  const queryClient = useQueryClient();
  const { setErrors } = useContext(RatingErrorsContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const agentAuthorityQK = glacierPolicyApiGetAgentAuthorityQueryKey({
    path: { account_id: policyId },
  });

  const printQueueKey = glacierPolicyApiGetPrintQueueQueryKey();

  const issuePolicy = useMutation({
    ...glacierPolicyApiIssuePolicyMutation(),
    onSuccess() {
      setIssuing(false);
      queryClient.refetchQueries({ queryKey: warehouseKey });
      queryClient.refetchQueries({ queryKey: policyQK });
      queryClient.refetchQueries({ queryKey: formulasQK });
      queryClient.refetchQueries({ queryKey: permissionsQK });
      queryClient.refetchQueries({
        queryKey: [{ _id: 'glacierPolicyApiGetObjectDetails' }],
        exact: false,
      });
      queryClient.refetchQueries({ queryKey: printQueueKey });
      setMessage({
        content:
          'Policy has been issued. Check the Document Warehouse for the Bundle',
        level: 'SUCCESS',
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape needs ApiError type (future ticket)
    onError(error: any) {
      setLoading(false);
      setIssuing(false);
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey: formulasQK });

      let title = 'Missing required fields for policy issuance';
      const content = formatErrorMessage(error);

      if (error.detail?.includes('Error(')) {
        title = error.detail.split(':')[0];
      }
      if (error.status === 422) {
        setErrors(error.errors || null);

        toast({
          title: 'Error',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <p className="text-white">
                Some required data is missing, please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('ratingError', 'true');
        navigate('?' + searchParams.toString());
        setMessage({
          content: 'Error Generating Policy Bundle.',
          level: 'ERROR',
        });
      } else if (error.status === 423) {
        // Freshest evaluation we have: write it even when empty so stale alerts clear.
        queryClient.setQueryData(agentAuthorityQK, error);
        if (flattenAuthorityErrors(error).length > 0) {
          setMessage({
            content: (
              <p className="inline-block">
                Please note, Agent Authority alerts. Please click{' '}
                <Link
                  to={`/policy/${policyId}/policy-decisions?tab=agentAuthorityErrors`}
                  className="underline"
                >
                  here
                </Link>{' '}
                to review
              </p>
            ),
            level: 'ERROR',
          });
        }
      } else {
        setMessage({
          content: (
            <ErrorDialog title={title} onClose={() => setMessage(undefined)}>
              {content}
            </ErrorDialog>
          ),
          level: 'ERROR',
        });
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Issue Policy
          </DialogTitle>
          <DialogDescription>
            Are you sure you wish to issue the Policy?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isIssuing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIssuing(true);
              setMessage({
                content: 'Generating Policy Bundle. Please wait...',
                level: 'INFO',
              });
              issuePolicy.mutate({
                path: { account_id: policyId },
              });
              onOpenChange(false);
            }}
            className="bg-brand-medium-green text-white hover:bg-brand-dark-green"
            disabled={isIssuing}
          >
            {isIssuing ? 'Issuing Policy...' : 'Issue Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LockPolicyDialog;
